import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoTrackItem, AudioTrackItem, TextTrackItem, BgMusicTrackItem } from './items';
import VolumeSlider from '../forms/components/VolumeSlider';
import {
  Cog6ToothIcon,
  EyeIcon,
  EyeSlashIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  MusicalNoteIcon,
  BellSlashIcon
} from '@heroicons/react/24/outline';

interface TimelineTrack {
  id: string;
  name: string;
  type: 'video' | 'audio' | 'text' | 'bgmusic';
  clips: TimelineClip[];
}

interface TimelineClip {
  id: string;
  name: string;
  startTime: number;
  duration: number;
  color: string;
  videoClip?: any;
  isGenerating?: boolean;
  hasAudio?: boolean;
  hasText?: boolean;
  editInfo?: any;
}

interface TimelineTracksProps {
  tracks: TimelineTrack[];
  trackEnabled: { video: boolean; audio: boolean; text: boolean; bgmusic: boolean };
  videoVolume: number;
  audioVolume: number;
  bgmVolume: number;
  videoClipsMuted: boolean;
  audioClipsMuted: boolean;
  hasAudioClips: boolean;
  selectedClip: string | null;
  selectedTrackType: 'video' | 'audio' | 'text' | 'bgmusic' | null;
  isBgmTrackActive: boolean;
  currentTime: number;
  pixelsPerSecond: number;
  totalDuration: number;
  onTrackToggle: (trackType: 'text') => void;
  onSubtitleSettingsOpen?: () => void;
  onClipSelect: (clip: TimelineClip, trackType: 'video' | 'audio' | 'text' | 'bgmusic') => void;
  onInsertClip?: (afterClipId: string) => void;
  onDeleteClip?: (clipId: string) => void;
  onCopyClip?: (clipId: string) => void;
  onBgmSelect?: () => void;
  onVideoVolumeChange?: (volume: number) => void;
  onAudioVolumeChange?: (volume: number) => void;
  onBgmVolumeChange?: (volume: number) => void;
  onToggleVideoVolume?: (clipId: string, volume: number) => void;
  onToggleAudioVolume?: (clipId: string, volume: number) => void;
  onToggleAllVideoVolume?: () => void;
  onToggleAllAudioVolume?: () => void;
}

export default function TimelineTracks({
  tracks,
  trackEnabled,
  videoVolume,
  audioVolume,
  bgmVolume,
  videoClipsMuted,
  audioClipsMuted,
  hasAudioClips,
  selectedClip,
  selectedTrackType,
  isBgmTrackActive,
  currentTime,
  pixelsPerSecond,
  totalDuration,
  onTrackToggle,
  onSubtitleSettingsOpen,
  onClipSelect,
  onInsertClip,
  onDeleteClip,
  onCopyClip,
  onBgmSelect,
  onVideoVolumeChange,
  onAudioVolumeChange,
  onBgmVolumeChange,
  onToggleVideoVolume,
  onToggleAudioVolume,
  onToggleAllVideoVolume,
  onToggleAllAudioVolume
}: TimelineTracksProps) {
  const { t } = useTranslation(['editor', 'common']);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const getTrackIcon = (type: string) => {
    switch (type) {
      case 'video':
        return '🎬';
      case 'audio':
        return '🎵';
      case 'bgmusic':
        return '🎼';
      case 'text':
        return '📝';
      default:
        return '📄';
    }
  };

  // 用于同步滚动的 ref
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 计算时间线内容的总宽度
  const timelineWidth = Math.max(totalDuration * pixelsPerSecond + 100, 800);

  // 滚轮横向滚动
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      e.preventDefault();
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  const renderBatchMuteButton = (
    muted: boolean,
    onClick: (() => void) | undefined,
    labels: { enable: string; disable: string },
    disabled = false,
  ) => (
    <button
      onClick={onClick}
      disabled={!onClick || disabled}
      className="transition-colors cursor-pointer flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
      title={muted ? labels.enable : labels.disable}
      aria-label={muted ? labels.enable : labels.disable}
    >
      {muted ? (
        <SpeakerXMarkIcon className="w-3 h-3 text-gray-500" />
      ) : (
        <SpeakerWaveIcon className="w-3 h-3 text-green-500" />
      )}
    </button>
  );

  return (
    <div className="flex flex-col overflow-hidden" style={{ flex: '1 1 0', minWidth: 0, width: 0 }}>
      {/* 整体布局：左侧固定标签 + 右侧可滚动内容 */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* 左侧固定列：轨道标签 */}
        <div className="w-38 flex-shrink-0 flex flex-col bg-gray-800 border-r border-gray-700">
          {/* 轨道标题 */}
          <div className="h-7 flex items-center px-3 border-b border-gray-700">
            <span className="text-xs font-medium text-gray-400">{t('trackLabel')}</span>
          </div>
          {/* 轨道标签列表 */}
          <div className="flex-1 overflow-y-auto">
            {tracks.map((track) => (
              <div
                key={track.id}
                className={`border-b border-gray-700 flex items-center justify-between px-3 ${track.type === 'text' ? 'h-7' : track.type === 'video' ? 'h-20' : track.type === 'bgmusic' ? 'h-7' : 'h-7'
                  }`}
              >
                <div className="flex items-center min-w-0">
                  <span className="text-lg mr-2">{getTrackIcon(track.type)}</span>
                  <span className="text-sm text-gray-300 truncate">{track.name}</span>
                  {track.type === 'bgmusic' && onBgmSelect && (
                    <button
                      onClick={onBgmSelect}
                      className="ml-1 p-1 text-gray-400 hover:text-blue-400 transition-colors"
                      title={t('selectBgm')}
                    >
                      <Cog6ToothIcon className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {track.type === 'text' ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {onSubtitleSettingsOpen && (
                      <button
                        onClick={onSubtitleSettingsOpen}
                        className="text-gray-400 hover:text-blue-400 transition-colors flex items-center justify-center flex-shrink-0"
                        title={t('subtitleSettings.open')}
                      >
                        <Cog6ToothIcon className="w-3 h-3" />
                      </button>
                    )}
                    <button
                      onClick={() => onTrackToggle('text')}
                      className="transition-colors flex items-center justify-center flex-shrink-0"
                      title={trackEnabled.text ? t('subtitleSettings.hide') : t('subtitleSettings.show')}
                    >
                      {trackEnabled.text ? (
                        <EyeIcon className="w-3 h-3 text-green-500" />
                      ) : (
                        <EyeSlashIcon className="w-3 h-3 text-gray-500" />
                      )}
                    </button>
                  </div>
                ) : track.type === 'video' ? (
                  (onVideoVolumeChange || onToggleAllVideoVolume) && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {renderBatchMuteButton(
                        videoClipsMuted,
                        onToggleAllVideoVolume,
                        {
                          enable: t('timeline.videoTrackItem.enableAllSound'),
                          disable: t('timeline.videoTrackItem.disableAllSound'),
                        },
                      )}
                      {onVideoVolumeChange && (
                        <VolumeSlider
                          value={videoVolume}
                          onChange={onVideoVolumeChange}
                        />
                      )}
                    </div>
                  )
                ) : track.type === 'audio' ? (
                  (onAudioVolumeChange || onToggleAllAudioVolume) && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {renderBatchMuteButton(
                        audioClipsMuted,
                        onToggleAllAudioVolume,
                        {
                          enable: t('timeline.audioTrackItem.enableAllSound'),
                          disable: t('timeline.audioTrackItem.disableAllSound'),
                        },
                        !hasAudioClips,
                      )}
                      {onAudioVolumeChange && (
                        <VolumeSlider
                          value={audioVolume}
                          onChange={onAudioVolumeChange}
                        />
                      )}
                    </div>
                  )
                ) : track.type === 'bgmusic' ? (
                  onBgmVolumeChange && (
                    <VolumeSlider
                      value={bgmVolume}
                      onChange={onBgmVolumeChange}
                    />
                  )
                ) : null}
              </div>
            ))}
          </div>
        </div>

        {/* 右侧可滚动区域：时间标尺 + 轨道内容 */}
        <div
          ref={scrollContainerRef}
          className="overflow-x-scroll overflow-y-hidden relative"
          style={{ flex: '1 1 0', minWidth: 0, width: 0 }}
          onWheel={handleWheel}
        >
          {/* 内容容器 - 宽度为 timelineWidth，但最小 100% */}
          <div style={{ minWidth: '100%', width: `${timelineWidth}px` }}>
            {/* 时间标尺 */}
            <div className="h-7 bg-gray-750 border-b border-gray-700 relative">
              {Array.from({ length: Math.ceil(totalDuration / 10) + 1 }, (_, i) => (
                <div
                  key={i}
                  className="absolute top-0 h-full flex items-center"
                  style={{ left: `${i * 10 * pixelsPerSecond}px` }}
                >
                  <div className="w-px h-4 bg-gray-600"></div>
                  <span className="text-xs text-gray-400 ml-1">{formatTime(i * 10)}</span>
                </div>
              ))}
            </div>

            {/* 轨道内容 */}
            <div className="relative">
              {tracks.map((track) => (
                <div
                  key={track.id}
                  className={`relative overflow-visible border-b border-gray-700 ${track.type === 'text' ? 'h-7' : track.type === 'video' ? 'h-20' : track.type === 'bgmusic' ? 'h-7' : 'h-7'
                    } ${selectedTrackType === track.type && track.type !== 'bgmusic' ? 'bg-blue-900/20' : ''
                    } ${track.type === 'bgmusic' && isBgmTrackActive ? 'bg-blue-900/20' : ''
                    }`}
                >
                  {track.clips.map((clip, clipIndex) => {
                    // Extract base ID from clip ID (remove prefixes like 'subtitle-')
                    const getBaseId = (id: string) => {
                      if (id.startsWith('subtitle-')) return id.replace('subtitle-', '');
                      if (id.startsWith('audio-')) return id.replace('audio-', '');
                      return id;
                    };

                    const clipBaseId = getBaseId(clip.id);
                    const belongsToSelectedClip = clipBaseId === selectedClip || clip.id === selectedClip;
                    const isTrackActive = selectedTrackType === track.type;

                    // 背景音乐轨道：根据isBgmTrackActive状态显示激活
                    // 其他轨道：只有当前轨道类型匹配时才显示激活状态（高亮）
                    let shouldShowSelected;
                    if (track.type === 'bgmusic') {
                      // 背景音乐轨道：根据激活状态显示
                      shouldShowSelected = isBgmTrackActive;
                    } else {
                      // 其他轨道：属于选中clip且是当前激活的轨道才高亮
                      shouldShowSelected = belongsToSelectedClip && selectedTrackType === track.type;
                    }

                    const commonProps = {
                      clip,
                      pixelsPerSecond,
                      isSelected: shouldShowSelected,
                      onSelect: () => {
                        onClipSelect(clip, track.type);
                      },
                      onInsertClip: track.type === 'video' ? onInsertClip : undefined,
                      onDeleteClip: track.type === 'video' ? onDeleteClip : undefined,
                      onCopyClip: track.type === 'video' ? onCopyClip : undefined,
                      onToggleVolume: track.type === 'video'
                        ? onToggleVideoVolume
                        : track.type === 'audio'
                          ? onToggleAudioVolume
                          : undefined
                    };

                    switch (track.type) {
                      case 'video':
                        return <VideoTrackItem key={clip.id} {...commonProps} />;
                      case 'audio':
                        return <AudioTrackItem key={clip.id} {...commonProps} />;
                      case 'text':
                        return <TextTrackItem key={clip.id} {...commonProps} />;
                      case 'bgmusic':
                        return <BgMusicTrackItem key={clip.id} {...commonProps} />;
                      default:
                        return null;
                    }
                  })}
                </div>
              ))}

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                style={{ left: `${currentTime * pixelsPerSecond}px` }}
              >
                <div className="w-3 h-3 bg-red-500 rounded-full -translate-x-1/2 -translate-y-1/2 absolute top-0"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
